import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { RecommendationSource, RecommendedMenu } from '../../API';
import ProgrammePreviewNavigationBar from './programme_preview_navigation_bar';
import ProgrammePlanBoardPreview from './programme_plan_board_preview';
import { recommendMealsOperation } from '../../operations/recommender_operations';
import {
  DEFAULT_PROGRAMME_CALORIE_SPLITS,
  DEFAULT_PROGRAMME_MEAL_TYPES,
  calorieSplitsAreValid,
} from '../../services/meal_types';
import { buildExistingMealsFromProgrammePrescribedMeals } from '../../services/recommender';
import { userLocale } from '../../services/users';
import {
  useGetFullMealsByParentsQuery,
  useGetProgrammesQuery,
} from '../../app/smorgAppSyncGraphQLApi';
import { flattenObjects } from '../../services/arrays';
import { useGetUserProfileQuery } from '../../app/smorgAppSyncGraphQLApiCustomHooks';

interface ProgrammeViewProps {
  programmeId: string;
  planId: string;
}

const SIMULATED_USER_DAILY_CALORIE_TARGET = 2000;

const ProgrammePlanPreview = ({ programmeId, planId }: ProgrammeViewProps) => {
  const { data: userProfile } = useGetUserProfileQuery();

  const [isLoading, setIsLoading] = useState(true);
  const [recommendedMenu, setRecommendedMenu] =
    useState<RecommendedMenu | null>(null);
  const [recommenderErrorMessage] = useState<string | null>(null);

  const { data: ownedProgrammes } = useGetProgrammesQuery(undefined);

  const programme = (ownedProgrammes || []).find((p) => p.id === programmeId);

  const { data: fullMealsByParents, isLoading: isLoadingFullMeals } =
    useGetFullMealsByParentsQuery(programme?.recipesBoardIDs || [], {
      skip: (programme?.recipesBoardIDs || []).length === 0,
    });

  const fullMeals = useMemo(
    () => flattenObjects(Object.values(fullMealsByParents || {})),
    [fullMealsByParents],
  );

  const locales = useMemo(
    () => programme?.locales || [userLocale(userProfile)],
    [programme?.locales, userProfile],
  );

  const dispatch = useDispatch();

  useEffect(() => {
    if (!programme) {
      return;
    }

    if (isLoadingFullMeals) {
      return;
    }

    const currentPlan = programme.plans.find((p) => p.id === planId);

    if (!currentPlan) {
      return;
    }

    const programmeHasValidMealTypesAndCalorieSplits =
      (programme.mealTypes || []).length > 0 &&
      calorieSplitsAreValid(programme.mealTypes, programme.calorieSplits);

    const mealTypes = programmeHasValidMealTypesAndCalorieSplits
      ? programme.mealTypes
      : DEFAULT_PROGRAMME_MEAL_TYPES;

    const calorieSplits = programmeHasValidMealTypesAndCalorieSplits
      ? programme.calorieSplits
      : DEFAULT_PROGRAMME_CALORIE_SPLITS;

    const existingDays = currentPlan.days.map((day, indexZeroBased) => ({
      dayIndex: indexZeroBased + 1,
      entries: buildExistingMealsFromProgrammePrescribedMeals(
        day.entries,
        fullMeals,
      ),
    }));
    if (!programme.copyMealsExactly) {
      setIsLoading(true);
      const parentIDs = [...(programme.recipesBoardIDs || [])];
      if (programme.recipesBoard?.id) {
        parentIDs.push(programme.recipesBoard?.id);
      }
      recommendMealsOperation(
        mealTypes,
        7,
        existingDays,
        1,
        programme.nutritionConstraints,
        {},
        parentIDs,
        calorieSplits,
        1,
        programme.personalisedMealScaling,
        [RecommendationSource.SMORG],
        SIMULATED_USER_DAILY_CALORIE_TARGET,
        locales,
      )
        .then((menu: RecommendedMenu) => {
          setRecommendedMenu(menu);
        })
        .finally(() => setIsLoading(false));
    } else {
      setRecommendedMenu({ days: existingDays } as any as RecommendedMenu);
    }
  }, [
    programmeId,
    planId,
    dispatch,
    programme,
    fullMeals,
    locales,
    isLoadingFullMeals,
  ]);

  if (!programme) {
    return null;
  }

  const currentPlan = programme.plans.find((p) => p.id === planId);

  if (!currentPlan) {
    return null;
  }

  return (
    <>
      <ProgrammePreviewNavigationBar
        selectedProgrammeId={programmeId}
        selectedPlanId={currentPlan.id}
        calorieTarget={SIMULATED_USER_DAILY_CALORIE_TARGET}
      />
      <ProgrammePlanBoardPreview
        programme={programme}
        recommendedMenu={recommendedMenu}
        recommenderErrorMessage={recommenderErrorMessage}
        isLoading={isLoading}
      />
    </>
  );
};

export default ProgrammePlanPreview;
